import React, { useState, useEffect } from 'react';
import './App.css';
import { database } from './firebaseConfig';
import { ref, push, set } from 'firebase/database';

function App() {
  const [platform, setPlatform] = useState('unknown');
  const [householdName, setHouseholdName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(''); // 'success', 'error', or ''
  const [emailMessage, setEmailMessage] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform('ios');
    } else {
      setPlatform('desktop');
    }

    // Get invite details from URL parameters
    const params = new URLSearchParams(window.location.search);
    const household = params.get('household');
    const code = params.get('code');

    if (household) setHouseholdName(decodeURIComponent(household));
    if (code) setInviteCode(code);
  }, []);

  const handleDownload = () => {
    setDownloading(true);

    // Try to open the app first (deep link)
    const deepLink = inviteCode
      ? `kinvein://join?household=${encodeURIComponent(householdName)}&code=${inviteCode}`
      : 'kinvein://';

    // Attempt to open app with deep link
    window.location.href = deepLink;

    // Fallback to store if app doesn't open (after 2 seconds)
    const fallbackTimeout = setTimeout(() => {
      if (platform === 'ios') {
        // iOS Universal Link (opens app if installed, otherwise goes to App Store)
        window.location.href = `https://kinvein.app/join?household=${encodeURIComponent(householdName)}&code=${inviteCode}`;

        // Fallback to App Store after 1 second
        setTimeout(() => {
          window.location.href = 'https://apps.apple.com/app/kinvein/YOUR_APP_ID';
        }, 1000);
      } else if (platform === 'android') {
        // Android App Link / Intent
        const intent = `intent://join?household=${encodeURIComponent(householdName)}&code=${inviteCode}#Intent;scheme=kinvein;package=app.kinvein;S.browser_fallback_url=https://play.google.com/store/apps/details?id=app.kinvein;end`;
        window.location.href = intent;
      } else {
        alert('Please open this link on your mobile device to download KinVein!');
        setDownloading(false);
      }
    }, 2000);

    // Clear timeout if page loses visibility (means app opened)
    const visibilityHandler = () => {
      if (document.hidden) {
        clearTimeout(fallbackTimeout);
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  };

  const getDownloadButtonText = () => {
    if (downloading) return 'Opening Store...';
    if (platform === 'ios') return 'Download on App Store';
    if (platform === 'android') return 'Get it on Google Play';
    return 'Download KinVein';
  };

  const getPlatformIcon = () => {
    if (platform === 'ios') return '🍎';
    if (platform === 'android') return '🤖';
    return '📱';
  };

  const validateEmail = (email) => {
    // RFC 5322 compliant email regex (simplified version)
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    // Additional sanitization checks
    if (!email || email.length > 254) return false;
    if (!emailRegex.test(email)) return false;

    // Check for SQL injection patterns
    const sqlPatterns = /(\bOR\b|\bAND\b|\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|--|;|\/\*|\*\/|xp_|sp_)/i;
    if (sqlPatterns.test(email)) return false;

    // Check for script injection
    const scriptPatterns = /<script|javascript:|onerror=|onload=/i;
    if (scriptPatterns.test(email)) return false;

    return true;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    // Reset status
    setEmailStatus('');
    setEmailMessage('');

    // Validate email
    if (!validateEmail(email)) {
      setEmailStatus('error');
      setEmailMessage('Please enter a valid email address');
      return;
    }

    try {
      // Save to Firebase Realtime Database
      const emailListRef = ref(database, 'launch-notifications');
      const newEmailRef = push(emailListRef);

      await set(newEmailRef, {
        email: email.toLowerCase().trim(),
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        platform: platform
      });

      setEmailStatus('success');
      setEmailMessage('Thanks! We\'ll notify you when we launch.');
      setEmail('');

      // Close modal and clear success message after 3 seconds
      setTimeout(() => {
        setEmailStatus('');
        setEmailMessage('');
        setShowModal(false);
      }, 3000);

    } catch (error) {
      console.error('Error saving email:', error);
      setEmailStatus('error');
      setEmailMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="App">
      <div className="container">
        {/* Header */}
        <div className="header">
          <img src="/logo.svg" alt="KinVein Logo" className="logo" width={180} height={180} />
        </div>

        {/* Coming Soon Banner */}
        <div className="coming-soon-banner">
          <div className="coming-soon-icon">🚀</div>
          <h3 className="coming-soon-title">Coming Soon</h3>
          <p className="coming-soon-text">KinVein is launching soon on iOS and Android</p>
          <p className="coming-soon-description">
            Join the waitlist and be among the first to experience seamless family coordination.
            Get exclusive early access when we launch!
          </p>

          <button
            className="notify-me-button"
            onClick={() => setShowModal(true)}
          >
            <span className="bell-icon">🔔</span>
            Get Notified
          </button>
        </div>

        {/* Email Signup Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>

              <h2 className="modal-title">Get Launch Updates</h2>
              <p className="modal-subtitle">Be the first to know when KinVein launches!</p>

              <form onSubmit={handleEmailSubmit} className="email-signup-form">
                <div className="email-input-container">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="email-input"
                    required
                    autoFocus
                  />
                  <button type="submit" className="notify-button">
                    Notify Me
                  </button>
                </div>
                {emailMessage && (
                  <p className={`email-message ${emailStatus}`}>
                    {emailMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="content">
          {householdName ? (
            <>
              <div className="invite-card">
                <div className="invite-icon">🏠</div>
                <h2 className="invite-title">You've been invited!</h2>
                <p className="household-name">{householdName}</p>
                <p className="invite-message">
                  wants you to join their home on KinVein
                </p>
              </div>

              <div className="features">
                <div className="feature">
                  <span className="feature-icon">🍽️</span>
                  <span className="feature-text">Coordinate dinners</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">✅</span>
                  <span className="feature-text">Share tasks</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📝</span>
                  <span className="feature-text">Keep notes</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🔔</span>
                  <span className="feature-text">Send reminders</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2 className="hero-title">Keep your home in sync</h2>
              <p className="hero-subtitle">
                Coordinate dinners, share tasks, and stay connected with your family.
              </p>
            </>
          )}

          {/* Download Button */}
          <button
            className="download-button"
            onClick={handleDownload}
            disabled={downloading || platform === 'desktop'}
          >
            <span className="button-icon">{getPlatformIcon()}</span>
            <span className="button-text">{getDownloadButtonText()}</span>
          </button>

          {platform === 'desktop' && (
            <p className="desktop-message">
              📱 Open this link on your iPhone or Android device to download
            </p>
          )}

          {inviteCode && (
            <div className="invite-code-card">
              <p className="invite-code-label">Your invite code:</p>
              <p className="invite-code">{inviteCode}</p>
              <p className="invite-code-hint">
                Enter this code in the app to join the home
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="footer">
          <p className="footer-text">
            © {new Date().getFullYear()} KinVein. Keep your family coordinated.
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
